import React, { useState, useEffect, useCallback } from 'react';
import { documentApi } from '../../services/documentApi';
import type { Document, MaterialType } from '../../types/document';
import { DocumentStatus, MaterialType as MT } from '../../types/document';

const PAGE_SIZE = 50;

const ICONS = {
  search: (
    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  ),
  refresh: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  ),
  add: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
    </svg>
  ),
  file: (
    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  novel: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  ),
  technique: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.512.512a2.5 2.5 0 01-3.536 0l-.512-.512z" />
    </svg>
  ),
  other: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-10 4h4" />
    </svg>
  ),
  chevronDown: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  ),
  chevronRight: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  ),
  eye: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  ),
  trash: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  ),
  empty: (
    <svg className="w-16 h-16 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  loadingLarge: <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600" />,
  loadingSmall: <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600" />,
} as const;

const MATERIAL_TYPE_CONFIG: Record<MaterialType, { label: string; icon: JSX.Element }> = {
  [MT.NOVEL]: { label: '小说正文', icon: ICONS.novel },
  [MT.TECHNIQUE]: { label: '写作技巧', icon: ICONS.technique },
  [MT.OTHER]: { label: '其他资料', icon: ICONS.other },
};

const STATUS_CONFIG: Record<DocumentStatus, { label: string; className: string }> = {
  [DocumentStatus.UPLOADED]: { label: '已上传', className: 'bg-gray-100 text-gray-700' },
  [DocumentStatus.PARSING]: { label: '解析中', className: 'bg-blue-100 text-blue-700' },
  [DocumentStatus.SLICING]: { label: '切片中', className: 'bg-yellow-100 text-yellow-700' },
  [DocumentStatus.INDEXING]: { label: '索引中', className: 'bg-purple-100 text-purple-700' },
  [DocumentStatus.COMPLETED]: { label: '已完成', className: 'bg-green-100 text-green-700' },
  [DocumentStatus.FAILED]: { label: '失败', className: 'bg-red-100 text-red-700' },
  [DocumentStatus.UNINDEXED]: { label: '未索引', className: 'bg-orange-100 text-orange-700' },
};

export interface MaterialListProps {
  workspaceId: number;
  onDocumentSelect?: (document: Document) => void;
  className?: string;
}

const MaterialItem: React.FC<{
  document: Document;
  isSelected: boolean;
  onClick: () => void;
  onDelete: () => void;
}> = React.memo(({ document, isSelected, onClick, onDelete }) => {
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const statusConfig = STATUS_CONFIG[document.status] || STATUS_CONFIG[DocumentStatus.UPLOADED];

  return (
    <div
      onClick={onClick}
      className={`
        p-3 rounded-xl cursor-pointer transition-all duration-150
        ${isSelected ? 'bg-gray-100 border-l-4 border-gray-800' : 'hover:bg-gray-50 border-l-4 border-transparent'}
      `}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {ICONS.file}
            <h3 className={`font-medium truncate ${isSelected ? 'text-gray-900' : 'text-gray-800'}`} title={document.filename}>
              {document.filename}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-0.5 rounded-md ${statusConfig.className}`}>
              {statusConfig.label}
            </span>
            <span className="text-xs text-gray-500">{formatFileSize(document.file_size)}</span>
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="p-1 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
          title="删除"
        >
          {ICONS.trash}
        </button>
      </div>
    </div>
  );
});

const MaterialGroup: React.FC<{
  type: MaterialType;
  documents: Document[];
  selectedId: string | null;
  expanded: boolean;
  onToggle: () => void;
  onSelect: (doc: Document) => void;
  onDelete: (doc: Document) => void;
}> = ({ type, documents, selectedId, expanded, onToggle, onSelect, onDelete }) => {
  const config = MATERIAL_TYPE_CONFIG[type];

  return (
    <div className="mb-2">
      <div
        onClick={onToggle}
        className="flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
      >
        <span className="text-gray-500">
          {expanded ? ICONS.chevronDown : ICONS.chevronRight}
        </span>
        <span className="text-gray-600">{config.icon}</span>
        <span className="font-medium text-gray-800">{config.label}</span>
        <span className="text-xs text-gray-400 ml-auto">{documents.length}个</span>
      </div>
      {expanded && documents.length > 0 && (
        <div className="mt-1 space-y-1 border-l-2 border-gray-200 ml-2 pl-2">
          {documents.map((doc) => (
            <MaterialItem
              key={doc.id}
              document={doc}
              isSelected={selectedId === doc.id}
              onClick={() => onSelect(doc)}
              onDelete={() => onDelete(doc)}
            />
          ))}
        </div>
      )}
      {expanded && documents.length === 0 && (
        <div className="px-3 py-4 text-sm text-gray-500 text-center">
          暂无素材
        </div>
      )}
    </div>
  );
};

export const MaterialList: React.FC<MaterialListProps> = ({
  workspaceId,
  onDocumentSelect,
  className = ''
}) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Record<MaterialType, boolean>>({
    [MT.NOVEL]: true,
    [MT.TECHNIQUE]: true,
    [MT.OTHER]: true,
  });

  const loadDocuments = useCallback(async () => {
    if (!workspaceId || workspaceId <= 0) {
      setError('无效的工作区 ID');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await documentApi.listDocuments(undefined, {
        workspace_id: workspaceId,
        page: 1,
        page_size: PAGE_SIZE,
      });

      setDocuments(response.documents);
    } catch (err) {
      console.error('[MaterialList] 加载文档列表失败:', err);
      setError('加载文档列表失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleSelect = useCallback((doc: Document) => {
    setSelectedId(doc.id);
    onDocumentSelect?.(doc);
  }, [onDocumentSelect]);

  const handleDelete = useCallback(async (document: Document) => {
    if (!confirm(`确定要删除 "${document.filename}" 吗？`)) {
      return;
    }

    try {
      await documentApi.deleteDocument(undefined, document.id);
      setDocuments((prev) => prev.filter((doc) => doc.id !== document.id));
      if (selectedId === document.id) {
        setSelectedId(null);
      }
    } catch (err) {
      console.error('[MaterialList] 删除文档失败:', err);
      alert('删除文档失败，请稍后重试');
    }
  }, [selectedId]);

  const toggleGroup = useCallback((type: MaterialType) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  }, []);

  const filteredDocuments = documents.filter((doc) =>
    doc.filename.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groupedDocuments: Record<MaterialType, Document[]> = {
    [MT.NOVEL]: filteredDocuments.filter((doc) => doc.material_type === MT.NOVEL),
    [MT.TECHNIQUE]: filteredDocuments.filter((doc) => doc.material_type === MT.TECHNIQUE),
    [MT.OTHER]: filteredDocuments.filter((doc) => doc.material_type === MT.OTHER),
  };

  return (
    <div className={`bg-white h-full flex flex-col ${className}`}>
      {/* 顶部搜索栏 */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2">
              {ICONS.search}
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索素材..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white transition-all"
            />
          </div>
          <button
            onClick={loadDocuments}
            disabled={loading}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="刷新"
          >
            {loading ? ICONS.loadingSmall : ICONS.refresh}
          </button>
        </div>
      </div>

      {/* 素材列表 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              {ICONS.loadingLarge}
              <p className="mt-2 text-sm text-gray-600">加载中...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-64 px-4">
            <div className="text-red-500 mb-2 text-4xl">!</div>
            <p className="text-sm text-red-600 mb-3">{error}</p>
            <button
              onClick={loadDocuments}
              className="px-4 py-2 text-sm bg-gray-800 hover:bg-gray-700 text-white rounded-lg shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-150"
            >
              重试
            </button>
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 px-4">
            {ICONS.empty}
            <p className="text-sm text-gray-600 mb-3">{searchQuery ? '未找到匹配的素材' : '暂无素材'}</p>
            {!searchQuery && (
              <button
                onClick={() => {
                  const event = new CustomEvent('material:upload');
                  window.dispatchEvent(event);
                }}
                className="px-4 py-2 text-sm bg-gray-800 hover:bg-gray-700 text-white rounded-lg shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-150 flex items-center justify-center gap-2"
              >
                {ICONS.add}
                上传素材
              </button>
            )}
          </div>
        ) : (
          <>
            {([MT.NOVEL, MT.TECHNIQUE, MT.OTHER] as MaterialType[]).map((type) => (
              <MaterialGroup
                key={type}
                type={type}
                documents={groupedDocuments[type]}
                selectedId={selectedId}
                expanded={expandedGroups[type]}
                onToggle={() => toggleGroup(type)}
                onSelect={handleSelect}
                onDelete={handleDelete}
              />
            ))}
          </>
        )}
      </div>

      {/* 新增按钮 - 非空状态时显示在底部 */}
      {!loading && !error && filteredDocuments.length > 0 && (
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={() => {
              const event = new CustomEvent('material:upload');
              window.dispatchEvent(event);
            }}
            className="w-full px-4 py-2 text-sm bg-gray-800 hover:bg-gray-700 text-white rounded-lg shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-150 flex items-center justify-center gap-2"
          >
            {ICONS.add}
            上传素材
          </button>
        </div>
      )}

      {/* 底部统计信息 */}
      {!loading && !error && filteredDocuments.length > 0 && (
        <div className="p-4 border-t border-gray-200 text-center text-xs text-gray-500">
          共 {filteredDocuments.length} 个素材
        </div>
      )}
    </div>
  );
};

export default MaterialList;
